import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon } from 'lucide-react';
import { Tag, TagScope, TagType } from './TagsManagement';
import { OrgUnitSelector } from '../orgunit/OrgUnitSelector';
import type { OrgUnit } from '../../financial/sharedFinancialData';
interface TagFormProps {
  tag: Tag | null;
  tagTypes: TagType[];
  onSave: (tag: Omit<Tag, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
  locale: string;
}
export function TagForm({
  tag,
  tagTypes,
  onSave,
  onCancel,
  locale
}: TagFormProps) {
  const [formData, setFormData] = useState<Omit<Tag, 'id' | 'createdAt' | 'updatedAt'>>({
    name: '',
    description: '',
    tagTypeId: null,
    tagTypeName: null,
    scope: 'Global',
    orgUnitId: undefined,
    orgUnitName: undefined,
    orgUnitPath: undefined,
    colourHex: '#3B82F6',
    isActive: true,
    activeFrom: null,
    activeTo: null
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrgUnit | null>(null);
  const [presetColors] = useState(['#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16', '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#D946EF', '#EC4899', '#F43F5E', '#6B7280' // Gray
  ]);
  // Initialize form data when editing a tag
  useEffect(() => {
    if (tag) {
      setFormData({
        name: tag.name,
        description: tag.description,
        tagTypeId: tag.tagTypeId,
        tagTypeName: tag.tagTypeName,
        scope: tag.scope,
        orgUnitId: tag.orgUnitId,
        orgUnitName: tag.orgUnitName,
        orgUnitPath: tag.orgUnitPath,
        colourHex: tag.colourHex,
        isActive: tag.isActive,
        activeFrom: tag.activeFrom,
        activeTo: tag.activeTo
      });
    }
  }, [tag]);
  // Handle form field changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    if (name === 'scope') {
      // Reset OrgUnit fields when scope changes to Global
      setFormData({
        ...formData,
        scope: value as TagScope,
        ...(value === 'Global' ? {
          orgUnitId: undefined,
          orgUnitName: undefined,
          orgUnitPath: undefined
        } : {})
      });
      setSelectedOrgUnit(null);
    } else if (name === 'tagTypeId') {
      // Update tag type name when tag type changes
      const selectedTagType = value ? tagTypes.find(tt => tt.id === value) : null;
      setFormData({
        ...formData,
        tagTypeId: value || null,
        tagTypeName: selectedTagType ? selectedTagType.name : null,
        // If tag type has a color palette, use the first color
        colourHex: selectedTagType && selectedTagType.colourPalette && selectedTagType.colourPalette.length > 0 ? selectedTagType.colourPalette[0] : formData.colourHex
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  // Handle OrgUnit selection
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: OrgUnit | null) => {
    setFormData({
      ...formData,
      orgUnitId: orgUnitId || undefined,
      orgUnitName: orgUnit?.name || undefined,
      orgUnitPath: orgUnit?.path || undefined
    });
    setSelectedOrgUnit(orgUnit);
  };
  // Handle color selection
  const handleColorChange = (color: string) => {
    setFormData({
      ...formData,
      colourHex: color
    });
  };
  // Validate form before submission
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Tag name is required';
    }
    if (formData.scope === 'OrgUnit' && !formData.orgUnitId) {
      newErrors.orgUnitId = 'Organisation unit must be selected';
    }
    // Validate date range
    if (formData.activeFrom && formData.activeTo) {
      const fromDate = new Date(formData.activeFrom);
      const toDate = new Date(formData.activeTo);
      if (fromDate > toDate) {
        newErrors.activeTo = 'Active To date must be after Active From date';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };
  // Get color palette from selected tag type
  const getTagTypeColorPalette = () => {
    if (formData.tagTypeId) {
      const selectedTagType = tagTypes.find(tt => tt.id === formData.tagTypeId);
      return selectedTagType && selectedTagType.colourPalette ? selectedTagType.colourPalette : presetColors;
    }
    return presetColors;
  };
  const colorPalette = getTagTypeColorPalette();
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-20111">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-20112">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20113">
          {tag ? 'Edit Tag' : 'Create Tag'}
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-20114">
          {tag ? 'Update the details of an existing tag' : 'Define a new tag for use across the system'}
        </p>
      </div>

      <form onSubmit={handleSubmit} data-id="element-20115">
        <div className="px-4 py-5 sm:p-6" data-id="element-20116">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-20117">
            {/* Scope */}
            <div className="sm:col-span-3" data-id="element-20118">
              <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-20119">
                Scope <span className="text-red-500" data-id="element-20120">*</span>
              </label>
              <div className="mt-1" data-id="element-20121">
                <select id="scope" name="scope" value={formData.scope} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-20122">
                  <option value="Global" data-id="element-20123">Global</option>
                  <option value="OrgUnit" data-id="element-20124">Organisation Unit</option>
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-20125">
                {formData.scope === 'Global' ? 'Available to all organisation units' : 'Specific to selected organisation unit'}
              </p>
            </div>

            {/* OrgUnit Selector */}
            {formData.scope === 'OrgUnit' && <div className="sm:col-span-3" data-id="element-20126">
                <OrgUnitSelector value={formData.orgUnitId || ''} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} error={errors.orgUnitId} data-id="element-20127" />
              </div>}

            <div className="sm:col-span-3" data-id="element-20128">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-20129">
                Tag Name <span className="text-red-500" data-id="element-20130">*</span>
              </label>
              <div className="mt-1" data-id="element-20131">
                <input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.name ? 'border-red-300' : ''}`} placeholder="e.g., Urgent, Project X, Department" data-id="element-20132" />
                {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-20133">{errors.name}</p>}
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-20134">
              <label htmlFor="tagTypeId" className="block text-sm font-medium text-gray-700" data-id="element-20135">
                Tag Type
              </label>
              <div className="mt-1" data-id="element-20136">
                <select id="tagTypeId" name="tagTypeId" value={formData.tagTypeId || ''} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-20137">
                  <option value="" data-id="element-20138">No Tag Type</option>
                  {tagTypes.map(tagType => <option key={tagType.id} value={tagType.id} data-id="element-20139">
                      {tagType.name}
                      {tagType.scope === 'OrgUnit' && tagType.orgUnitName ? ` (${tagType.orgUnitName})` : ''}
                    </option>)}
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-20140">
                Grouping tags by type helps with organization
              </p>
            </div>

            <div className="sm:col-span-6" data-id="element-20141">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-20142">
                Description
              </label>
              <div className="mt-1" data-id="element-20143">
                <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Optional description of the tag" data-id="element-20144" />
              </div>
            </div>

            <div className="sm:col-span-6" data-id="element-20145">
              <label className="block text-sm font-medium text-gray-700" data-id="element-20146">
                Tag Color
              </label>
              <div className="mt-2 flex flex-wrap gap-2" data-id="element-20147">
                {colorPalette.map(color => <button key={color} type="button" className={`h-8 w-8 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${formData.colourHex === color ? 'ring-2 ring-offset-2 ring-indigo-500' : ''}`} style={{
                backgroundColor: color
              }} onClick={() => handleColorChange(color)} aria-label={`Color ${color}`} data-id="element-20148" />)}
                <div className="flex items-center" data-id="element-20149">
                  <label htmlFor="customColor" className="sr-only" data-id="element-20150">
                    Custom color
                  </label>
                  <input type="color" id="customColor" name="colourHex" value={formData.colourHex} onChange={handleInputChange} className="h-8 w-8 rounded-md border-gray-300" data-id="element-20151" />
                  <span className="ml-2 text-sm text-gray-500" data-id="element-20152">Custom</span>
                </div>
              </div>
              <div className="mt-3 flex items-center space-x-2" data-id="element-20153">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{
                backgroundColor: `${formData.colourHex}20`,
                color: formData.colourHex,
                border: `1px solid ${formData.colourHex}`
              }} data-id="element-20154">
                  {formData.name || 'Tag Preview'}
                </span>
                <span className="text-sm text-gray-500" data-id="element-20155">Preview</span>
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-20156">
              <label htmlFor="activeFrom" className="block text-sm font-medium text-gray-700" data-id="element-20157">
                Active From
              </label>
              <div className="mt-1" data-id="element-20158">
                <input type="date" name="activeFrom" id="activeFrom" value={formData.activeFrom || ''} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-20159" />
              </div>
            </div>

            <div className="sm:col-span-3" data-id="element-20160">
              <label htmlFor="activeTo" className="block text-sm font-medium text-gray-700" data-id="element-20161">
                Active To
              </label>
              <div className="mt-1" data-id="element-20162">
                <input type="date" name="activeTo" id="activeTo" value={formData.activeTo || ''} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.activeTo ? 'border-red-300' : ''}`} data-id="element-20163" />
                {errors.activeTo && <p className="mt-1 text-sm text-red-600" data-id="element-20164">{errors.activeTo}</p>}
              </div>
            </div>

            <div className="sm:col-span-6" data-id="element-20165">
              <div className="flex items-start" data-id="element-20166">
                <div className="flex items-center h-5" data-id="element-20167">
                  <input id="isActive" name="isActive" type="checkbox" checked={formData.isActive} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-20168" />
                </div>
                <div className="ml-3 text-sm" data-id="element-20169">
                  <label htmlFor="isActive" className="font-medium text-gray-700" data-id="element-20170">
                    Active
                  </label>
                  <p className="text-gray-500" data-id="element-20171">
                    When checked, this tag will be available for use in the
                    system
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-20172">
          <button type="button" onClick={onCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20173">
            <XIcon className="h-4 w-4 mr-1.5" data-id="element-20174" />
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20175">
            <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-20176" />
            {tag ? 'Update Tag' : 'Create Tag'}
          </button>
        </div>
      </form>
    </div>;
}